import { AnimatePresence, motion } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  SpinnerGap, FloppyDisk, ArrowCounterClockwise, Check,
  Image as ImageIcon, MagicWand, Sparkle, PencilSimple,
} from "@phosphor-icons/react";
import { FieldLabel } from "./GraphicsHelpers";
import { ENHANCE_OPTIONS, PROVIDER_LABELS, PROVIDER_COLORS } from "@/lib/graphicsConstants";

export function GraphicsPreview({
  ratio, isBusy, generating, enhancing, editing, loadingMsg,
  selectedProvider, currentModelLabel, imageUrl, topic, isEnhanced,
  genId, saving, saved, showEnhanceMenu, setShowEnhanceMenu,
  showEditPanel, setShowEditPanel, editInstruction, setEditInstruction,
  onSave, onGenerate, onEnhance, onEdit,
}) {
  return (
    <div className="lg:col-span-2">
      <div className="sticky top-20 space-y-3">
        <FieldLabel>Preview</FieldLabel>
        <div className={`bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden flex items-center justify-center ${
          ratio === "9:16" || ratio === "4:5" ? "min-h-[420px]" : "min-h-[300px]"
        }`} data-testid="gfx-preview">
          <AnimatePresence mode="wait">
            {isBusy ? (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-3 p-8">
                <SpinnerGap size={40} className="animate-spin text-violet-400" />
                <p className="text-xs text-[var(--text-muted)] text-center">{loadingMsg}</p>
                <p className="text-[10px] text-[var(--text-muted)]">
                  via <span className={PROVIDER_COLORS[selectedProvider]}>{PROVIDER_LABELS[selectedProvider]}</span> / {currentModelLabel}
                </p>
              </motion.div>
            ) : imageUrl ? (
              <motion.div key="image" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full relative">
                <img src={imageUrl} alt={topic} className="w-full h-auto object-contain max-h-[500px]" data-testid="gfx-preview-image" />
                {isEnhanced && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-emerald-500/90 text-white text-[10px] font-bold rounded-full flex items-center gap-1" data-testid="gfx-enhanced-badge">
                    <Sparkle size={10} weight="fill" /> Enhanced
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div key="placeholder" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex flex-col items-center gap-2 p-8 text-center">
                <ImageIcon size={48} className="text-[var(--text-muted)] opacity-30" />
                <p className="text-xs text-[var(--text-muted)]">Your infographic will appear here</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Actions after generation */}
        {genId && !isBusy && (
          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
            {/* Save + Retry row */}
            <div className="flex gap-2">
              {!saved ? (
                <Button onClick={onSave} disabled={saving}
                  className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-xl gap-2" data-testid="gfx-save-btn">
                  {saving ? <SpinnerGap size={14} className="animate-spin" /> : <FloppyDisk size={16} />}
                  {saving ? "Saving..." : "Save to Library"}
                </Button>
              ) : (
                <div className="flex-1 h-10 flex items-center justify-center gap-2 text-emerald-400 text-sm font-medium bg-emerald-500/10 rounded-xl" data-testid="gfx-saved-indicator">
                  <Check size={16} weight="bold" /> Saved to Library
                </div>
              )}
              <Button onClick={onGenerate} variant="outline" className="h-10 px-4 rounded-xl gap-1.5" data-testid="gfx-regenerate-btn">
                <ArrowCounterClockwise size={14} />Retry
              </Button>
            </div>

            {/* Enhance + Edit row */}
            <div className="grid grid-cols-2 gap-2">
              {/* Enhance */}
              <div className="relative">
                <Button onClick={() => setShowEnhanceMenu(!showEnhanceMenu)}
                  variant="outline"
                  className="w-full h-10 rounded-xl gap-2 border-violet-500/30 text-violet-400 hover:bg-violet-500/10 hover:border-violet-500/50 text-xs"
                  data-testid="gfx-enhance-btn">
                  <MagicWand size={14} /> Enhance
                </Button>
                <AnimatePresence>
                  {showEnhanceMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                      className="absolute bottom-full mb-2 left-0 right-0 z-50 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl shadow-lg overflow-hidden"
                      data-testid="gfx-enhance-menu">
                      {ENHANCE_OPTIONS.map(opt => (
                        <button key={opt.key} onClick={() => onEnhance(opt.key)}
                          className="w-full text-left px-3 py-2.5 hover:bg-[var(--bg-hover)] transition-colors border-b border-[var(--border)] last:border-0"
                          data-testid={`gfx-enhance-${opt.key}`}>
                          <span className="text-xs font-medium text-[var(--text-primary)]">{opt.label}</span>
                          <span className="block text-[10px] text-[var(--text-muted)]">{opt.desc}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Edit */}
              <Button
                onClick={() => setShowEditPanel(!showEditPanel)}
                variant="outline"
                className="w-full h-10 rounded-xl gap-2 border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/50 text-xs"
                data-testid="gfx-edit-toggle-btn"
              >
                <PencilSimple size={14} /> Edit Image
              </Button>
            </div>

            {/* Edit Panel */}
            <AnimatePresence>
              {showEditPanel && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="bg-[var(--bg-card)] border border-amber-500/20 rounded-xl p-3 space-y-2" data-testid="gfx-edit-panel">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400">Edit Instruction</p>
                    <Textarea
                      value={editInstruction}
                      onChange={e => setEditInstruction(e.target.value.slice(0, 500))}
                      placeholder='e.g., "Remove the third item" or "Add a dollar sign icon next to each statistic" or "Change the title to ..."'
                      rows={2}
                      className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm resize-none"
                      data-testid="gfx-edit-instruction"
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-[9px] text-[var(--text-muted)]">{editInstruction.length}/500</p>
                      <Button
                        onClick={onEdit}
                        disabled={editing || editInstruction.trim().length < 5}
                        className="h-8 px-4 bg-amber-600 hover:bg-amber-500 text-white text-xs rounded-lg gap-1.5"
                        data-testid="gfx-apply-edit-btn"
                      >
                        {editing
                          ? <><SpinnerGap size={12} className="animate-spin" /> Applying...</>
                          : <><PencilSimple size={12} /> Apply Edit</>}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
}
